import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { LinkRecommendItem, LinkRecommendKind } from "./web_parts/types";

const COLLECTION = "linkitems_recommend";
const DOC_META = "meta";

export class LinkRecommendService {
  static docId(kind: LinkRecommendKind): string {
    return kind;
  }

  static async getMetaVersion(): Promise<number> {
    const snap = await getDoc(doc(db, COLLECTION, DOC_META));
    if (!snap.exists()) return 0;
    const v = snap.data()?.version;
    return typeof v === "number" ? v : Number(v ?? 0) || 0;
  }

  static async getItems(kind: LinkRecommendKind): Promise<LinkRecommendItem[]> {
    const snap = await getDoc(doc(db, COLLECTION, kind));
    if (!snap.exists()) return [];
    const raw = snap.data()?.items;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((row, index) => {
        if (!row || typeof row !== "object") return null;
        const r = row as Record<string, unknown>;
        const title = String(r.title ?? "").trim();
        const description = String(r.description ?? "").trim();
        const url = String(r.url ?? "").trim();
        const sortOrder =
          typeof r.sortOrder === "number" ? r.sortOrder : Number(r.sortOrder ?? index) || index;
        if (!title && !url) return null;
        return {
          localId: `item_${kind}_${index}_${title}`,
          title,
          description,
          url,
          sortOrder,
        } as LinkRecommendItem;
      })
      .filter((item): item is LinkRecommendItem => item != null)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * 指定 kind の一覧を保存し、meta.version をインクリメントする。
   * @returns 新しい version
   */
  static async saveItems(kind: LinkRecommendKind, items: LinkRecommendItem[]): Promise<number> {
    const now = Date.now();
    const currentVersion = await this.getMetaVersion();
    const nextVersion = currentVersion + 1;
    const payloadItems = items.map((item, index) => ({
      title: item.title.trim(),
      description: item.description.trim(),
      url: item.url.trim(),
      sortOrder: index,
      updateTime: now,
    }));

    await setDoc(
      doc(db, COLLECTION, kind),
      {
        kind,
        version: nextVersion,
        updatedAt: now,
        items: payloadItems,
      },
      { merge: true }
    );

    await setDoc(
      doc(db, COLLECTION, DOC_META),
      {
        version: nextVersion,
        updatedAt: now,
        lastKind: kind,
      },
      { merge: true }
    );

    return nextVersion;
  }

  /** 初回編集用のデフォルト（端末初期シードと同等） */
  static defaultItems(kind: LinkRecommendKind): LinkRecommendItem[] {
    if (kind === "web") {
      const rows: Array<[string, string, string]> = [
        ["秋の味覚", "秋が旬の食べ物一覧です", "https://harenohi.asahigroup-japan.co.jp/eat/2025/10/14/3-22/"],
        ["秋の観光地", "JTB推奨の秋の観光スポット", "https://www.jtb.co.jp/kokunai-guide/list/index.html?area=00&feature=7&genre=000"],
        ["台風の注意点", "東京消防庁の防災情報です", "https://www.tfd.metro.tokyo.lg.jp/lfe/bou_topic/flood.html"],
        ["日本の祝日・休日", "日本の祝日・休日カレンダー", "https://holidays-calendar.net/calendar/japan_calendar.html"],
        ["ネット将棋", "将棋が出来るサイトです。対戦も可能", "https://www.studiok-i.net/ps/"],
        ["ネット囲碁", "囲碁ができるサイトです。対戦も可能", "https://www.sakura-ent.net/igoonline/game?lang=ja"],
        ["Web麻雀", "麻雀ゲームができます", "https://webmahjong.appspot.com/"],
      ];
      return rows.map(([title, description, url], i) => ({
        localId: `default_web_${i}`,
        title,
        description,
        url,
        sortOrder: i,
      }));
    }
    const rows: Array<[string, string, string]> = [
      ["桜 名所", "春の時期に展開します", "桜 名所"],
      ["紅葉 名所", "秋の時期に展開します", "紅葉 名所"],
      ["お正月 昔の風景", "冬の時期に展開します", "昭和 お正月"],
      ["地震対策 高齢者向け", "地震などがあった場合に展開します", "地震対策 高齢者"],
      ["防災グッズ 使い方", "災害などがあった場合に展開します", "防災グッズ 使い方"],
    ];
    return rows.map(([title, description, url], i) => ({
      localId: `default_youtube_${i}`,
      title,
      description,
      url,
      sortOrder: i,
    }));
  }
}
